import {
  getPassioLocalizationConfigForBrandedSearch,
  getPassioLocalizationConfigForOrgLocale,
  type PassioFoodDataInfo,
} from "@notemeal/passio-utils";
import { useApolloClient } from "@apollo/client";
import { useDebounce } from "shared-ui/hooks/useDebounce";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocaleContext } from "shared-ui/contexts/Locale/LocaleContext";
import { searchPassioSemantic } from "./passioClient";
import { getPassioSearchAuth, invalidatePassioSearchAuth } from "./passioSearchAuth";

export const MIN_SEARCH_TERM_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 200;
const MAX_RESULTS = 50;

export type UsePassioFoodSearchOptions = {
  searchTerm: string;
  brandedOnly?: boolean;
  onSearchError?: (error: Error) => void;
};

export type UsePassioFoodSearchResult = {
  results: PassioFoodDataInfo[];
  alternatives: string[];
  loading: boolean;
  error: Error | null;
  searchConfigured: boolean | null;
};

export const usePassioFoodSearch = ({
  searchTerm,
  brandedOnly = true,
  onSearchError,
}: UsePassioFoodSearchOptions): UsePassioFoodSearchResult => {
  const apolloClient = useApolloClient();
  const { locale: orgLocale } = useLocaleContext();
  const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);
  const trimmedSearchTerm = searchTerm.trim();
  const trimmedDebouncedSearchTerm = debouncedSearchTerm.trim();

  const localization = useMemo(
    () => (brandedOnly ? getPassioLocalizationConfigForBrandedSearch(orgLocale) : getPassioLocalizationConfigForOrgLocale(orgLocale)),
    [orgLocale, brandedOnly]
  );

  const [results, setResults] = useState<PassioFoodDataInfo[]>([]);
  const [alternatives, setAlternatives] = useState<string[]>([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [searchConfigured, setSearchConfigured] = useState<boolean | null>(null);
  const requestIdRef = useRef(0);
  const onSearchErrorRef = useRef(onSearchError);
  onSearchErrorRef.current = onSearchError;

  const authContext = useMemo(
    () => ({
      getAuth: () => getPassioSearchAuth(apolloClient),
      invalidateAuth: invalidatePassioSearchAuth,
    }),
    [apolloClient]
  );

  const isSearchActive = trimmedSearchTerm.length >= MIN_SEARCH_TERM_LENGTH;
  const isDebouncing = isSearchActive && trimmedDebouncedSearchTerm !== trimmedSearchTerm;
  const shouldSearch = searchConfigured === true && trimmedDebouncedSearchTerm.length >= MIN_SEARCH_TERM_LENGTH;

  useEffect(() => {
    let cancelled = false;

    const checkSearchConfigured = async () => {
      try {
        await getPassioSearchAuth(apolloClient);
        if (!cancelled) {
          setSearchConfigured(true);
        }
      } catch (err) {
        if (!cancelled) {
          setSearchConfigured(false);
          const error = err instanceof Error ? err : new Error("Failed to check search configuration");
          if (error.message !== "Branded food search is not configured") {
            onSearchErrorRef.current?.(error);
          }
        }
      }
    };

    void checkSearchConfigured();

    return () => {
      cancelled = true;
    };
  }, [apolloClient]);

  useEffect(() => {
    if (!shouldSearch) return;

    const requestId = ++requestIdRef.current;
    let cancelled = false;

    const runSearch = async () => {
      setFetchLoading(true);
      setError(null);

      try {
        const response = await searchPassioSemantic(authContext, trimmedDebouncedSearchTerm, localization, MAX_RESULTS);

        if (cancelled || requestId !== requestIdRef.current) {
          return;
        }

        setResults(response.results.slice(0, MAX_RESULTS));
        setAlternatives(response.alternatives);
      } catch (err) {
        if (cancelled || requestId !== requestIdRef.current) {
          return;
        }

        const searchError = err instanceof Error ? err : new Error("Branded food search failed");
        setResults([]);
        setAlternatives([]);
        setError(searchError);
        onSearchErrorRef.current?.(searchError);
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setFetchLoading(false);
        }
      }
    };

    void runSearch();

    return () => {
      cancelled = true;
    };
  }, [shouldSearch, trimmedDebouncedSearchTerm, authContext, localization]);

  return {
    results: isSearchActive ? results : [],
    alternatives: isSearchActive ? alternatives : [],
    loading: isDebouncing || (isSearchActive && fetchLoading) || searchConfigured === null,
    error: isSearchActive ? error : null,
    searchConfigured,
  };
};
